import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  FileSpreadsheet, Eye, Clock, CheckCircle2, X, FileText,
  CheckSquare, Search, SlidersHorizontal, Loader2, AlertCircle
} from 'lucide-react';
import { Transaction } from '../types/dashboard';
import ViewTransactionModal from '../components/validation/ViewTransactionModal';
import ViewRegulModal from '../components/validation/ViewRegulModal';
import { formatCurrency } from '../utils/format';
import { useAuth } from '../contexts/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────
interface RegularizationTransaction extends Transaction {
  regul_status: number;
  total_requests?: number;
  regul_request?: {
    id: string;
    request_comment: string;
    created_at: string;
    requested_by: string;
    status: string;
    admin_comment?: string;
    reviewed_by?: string;
    reviewed_at?: string;
    user: { firstname: string; lastname: string };
    reviewer?: { firstname: string; lastname: string };
  };
}

// ── Config statuts demande ────────────────────────────────────────────────
const REQUEST_STATUS_CONFIG: Record<string, { label: string; dot: string; text: string; bg: string }> = {
  pending:  { label: 'En attente', dot: 'bg-amber-400',   text: 'text-amber-700',   bg: 'bg-amber-50' },
  approved: { label: 'Approuvée',  dot: 'bg-emerald-400', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  rejected: { label: 'Rejetée',    dot: 'bg-red-400',     text: 'text-red-600',     bg: 'bg-red-50' },
  treated:  { label: 'Traitée',    dot: 'bg-slate-400',   text: 'text-slate-600',   bg: 'bg-slate-100' },
};

const TX_STATUS_CONFIG: Record<string, { label: string; dot: string; text: string; bg: string }> = {
  potentiel: { label: 'Potentiel', dot: 'bg-amber-400',   text: 'text-amber-700',   bg: 'bg-amber-50' },
  compromis: { label: 'Compromis', dot: 'bg-blue-400',    text: 'text-blue-700',    bg: 'bg-blue-50' },
  acte:      { label: 'Acté',      dot: 'bg-violet-400',  text: 'text-violet-700',  bg: 'bg-violet-50' },
  encaisse:  { label: 'Encaissé',  dot: 'bg-emerald-400', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  cloture:   { label: 'Clôturé',   dot: 'bg-slate-400',   text: 'text-slate-600',   bg: 'bg-slate-100' },
};

const REGUL_STATUS_LABEL: Record<number, string> = {
  0: 'Aucune', 1: 'En cours', 2: 'Approuvée', 3: 'Validé', 4: 'Traitée'
};

// ── Composant principal ───────────────────────────────────────────────────
export default function RegularizationRequests() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<RegularizationTransaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<RegularizationTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isViewRegulModalOpen, setIsViewRegulModalOpen] = useState(false);
  const [isTreatModalOpen, setIsTreatModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<number[]>([1, 2, 3]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { loadData(); }, []);
  useEffect(() => { filterTransactions(); }, [transactions, statusFilter, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data: regulRequestsData, error: regulError } = await supabase
        .from('regul_requests')
        .select(`
          id, status, created_at, request_comment, requested_by,
          reviewed_by, reviewed_at, admin_comment,
          user:users!requested_by (firstname, lastname),
          reviewer:users!reviewed_by (firstname, lastname),
          transaction:transactions!transaction_id (
            id, property_number, seller, buyer, sale_price,
            fees_with_tax, fees_without_tax, mandate_type, property_origin,
            status, compromise_date, expected_acte_date,
            regul, regul_status, commentaire_regul,
            transaction_agencies (agency),
            transaction_negotiators (negotiator:negotiators (id, firstname, lastname), role, percentage),
            transaction_assistants (assistant:assistants (id, firstname, lastname), role),
            transaction_retrocessions (id, type, amount)
          )
        `)
        .order('created_at', { ascending: false });

      if (regulError) throw regulError;

      const groupedRequests = new Map<string, any[]>();
      (regulRequestsData || []).forEach(request => {
        const key = request.transaction.property_number;
        if (!groupedRequests.has(key)) groupedRequests.set(key, []);
        groupedRequests.get(key)!.push(request);
      });

      const formatted = Array.from(groupedRequests.entries()).map(([, requests]) => {
        const sorted = requests.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        const latest = sorted[0];
        return {
          id: latest.transaction.id,
          property_number: latest.transaction.property_number,
          seller: latest.transaction.seller,
          buyer: latest.transaction.buyer,
          sale_price: latest.transaction.sale_price,
          fees_with_tax: latest.transaction.fees_with_tax,
          fees_without_tax: latest.transaction.fees_without_tax,
          mandate_type: latest.transaction.mandate_type,
          property_origin: latest.transaction.property_origin,
          status: latest.transaction.status,
          compromise_date: latest.transaction.compromise_date,
          expected_acte_date: latest.transaction.expected_acte_date,
          regul: latest.transaction.regul,
          regul_status: latest.transaction.regul_status,
          commentaire_regul: latest.transaction.commentaire_regul,
          agencies: latest.transaction.transaction_agencies?.map((ta: any) => ta.agency) || [],
          negotiators: latest.transaction.transaction_negotiators?.map((tn: any) => ({
            id: tn.negotiator.id,
            name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
            role: tn.role, percentage: tn.percentage
          })) || [],
          assistants: latest.transaction.transaction_assistants?.map((ta: any) => ({
            id: ta.assistant.id,
            name: `${ta.assistant.firstname} ${ta.assistant.lastname}`,
            role: ta.role
          })) || [],
          retrocessions: latest.transaction.transaction_retrocessions?.map((tr: any) => ({
            id: tr.id, type: tr.type, amount: tr.amount
          })) || [],
          regul_request: {
            id: latest.id, request_comment: latest.request_comment,
            created_at: latest.created_at, requested_by: latest.requested_by,
            status: latest.status, admin_comment: latest.admin_comment,
            reviewed_by: latest.reviewed_by, reviewed_at: latest.reviewed_at,
            user: latest.user, reviewer: latest.reviewer
          },
          total_requests: requests.length,
        };
      });

      formatted.sort((a, b) =>
        new Date(b.regul_request.created_at).getTime() - new Date(a.regul_request.created_at).getTime()
      );
      setTransactions(formatted);
    } catch (err: any) {
      console.error('Error loading regularization requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    const statusMap: Record<string, number> = { pending: 1, approved: 2, rejected: 3, treated: 4 };
    let filtered = [...transactions];
    if (statusFilter.length > 0) {
      filtered = filtered.filter(t => {
        const n = statusMap[t.regul_request?.status ?? ''] || 0;
        return statusFilter.includes(n);
      });
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.property_number.toLowerCase().includes(s) ||
        t.seller.toLowerCase().includes(s) ||
        t.buyer.toLowerCase().includes(s) ||
        t.agencies.some(a => a.toLowerCase().includes(s)) ||
        t.negotiators.some(n => n.name.toLowerCase().includes(s))
      );
    }
    setFilteredTransactions(filtered);
  };

  const toggleStatus = (status: number) => {
    setStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleMarkAsTreated = async (transaction: RegularizationTransaction) => {
    if (!confirm('Êtes-vous sûr de vouloir marquer cette régularisation comme traitée ?')) return;
    try {
      setLoading(true);
      const { error: transactionError } = await supabase
        .from('transactions')
        .update({ regul_status: 4, regul: false, commentaire_regul: null })
        .eq('id', transaction.id);
      if (transactionError) throw transactionError;

      if (transaction.regul_request) {
        await supabase.from('regul_requests').update({
          status: 'approved',
          admin_comment: 'Régularisation marquée comme traitée',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString()
        }).eq('id', transaction.regul_request.id);
      }

      await supabase.from('transaction_logs').insert([{
        transaction_id: transaction.id, user_id: user?.id,
        event_type: 'regul_request',
        event_data: { action: 'treated', comment: 'Régularisation marquée comme traitée' }
      }]);

      loadData();
      setIsTreatModalOpen(false);
      setSelectedTransaction(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateNetFees = (t: Transaction) => {
    const r = t.retrocessions?.reduce((sum, r) => sum + r.amount, 0) || 0;
    return t.fees_without_tax - r;
  };

  const formatDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  // KPI counts
  const kpis = {
    pending:  transactions.filter(t => t.regul_request?.status === 'pending').length,
    approved: transactions.filter(t => t.regul_request?.status === 'approved').length,
    rejected: transactions.filter(t => t.regul_request?.status === 'rejected').length,
    treated:  transactions.filter(t => t.regul_request?.status === 'treated').length,
  };

  const STATUS_FILTER_OPTIONS = [
    { value: 1, label: 'En attente',  dot: 'bg-amber-400',   active: 'bg-amber-50 border-amber-300 text-amber-700',   inactive: 'bg-white border-slate-200 text-slate-500' },
    { value: 2, label: 'Approuvée',   dot: 'bg-emerald-400', active: 'bg-emerald-50 border-emerald-300 text-emerald-700', inactive: 'bg-white border-slate-200 text-slate-500' },
    { value: 3, label: 'Rejetée',     dot: 'bg-red-400',     active: 'bg-red-50 border-red-300 text-red-600',         inactive: 'bg-white border-slate-200 text-slate-500' },
    { value: 4, label: 'Traitée',     dot: 'bg-slate-400',   active: 'bg-slate-100 border-slate-300 text-slate-600',  inactive: 'bg-white border-slate-200 text-slate-500' },
  ];

  if (loading && transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-400">
        <Loader2 size={28} className="animate-spin text-indigo-500" />
        <span className="text-sm">Chargement…</span>
      </div>
    );
  }

  const thCls = 'px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap';
  const tdCls = 'px-4 py-3.5 text-sm whitespace-nowrap';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-7 py-6 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-violet-500/10 rounded-full translate-y-1/2 pointer-events-none" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
            <FileSpreadsheet size={22} className="text-white/80" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Demandes de Régularisation</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              <span className="text-indigo-300 font-semibold">{filteredTransactions.length}</span>
              {' '}demande{filteredTransactions.length !== 1 ? 's' : ''} affichée{filteredTransactions.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
          <AlertCircle size={15} className="shrink-0" />
          {error}
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { key: 'pending',  label: 'En attente', count: kpis.pending,  icon: Clock,         iconBg: 'bg-amber-100',   iconColor: 'text-amber-600',   bar: 'bg-amber-400' },
          { key: 'approved', label: 'Approuvées', count: kpis.approved, icon: CheckCircle2,  iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', bar: 'bg-emerald-400' },
          { key: 'rejected', label: 'Rejetées',   count: kpis.rejected, icon: X,             iconBg: 'bg-red-100',     iconColor: 'text-red-600',     bar: 'bg-red-400' },
          { key: 'treated',  label: 'Traitées',   count: kpis.treated,  icon: FileText,      iconBg: 'bg-slate-100',   iconColor: 'text-slate-600',   bar: 'bg-slate-400' },
        ].map(({ label, count, icon: Icon, iconBg, iconColor, bar }) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-start justify-between mb-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
              <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
                <Icon size={16} className={iconColor} />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900">{count}</p>
            <div className="mt-3 h-0.5 rounded-full bg-slate-100">
              <div className={`h-full rounded-full opacity-60 ${bar}`} style={{ width: count > 0 ? `${Math.min(100, (count / Math.max(transactions.length, 1)) * 100)}%` : '0%' }} />
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters card ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2.5 rounded-t-2xl">
          <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
            <SlidersHorizontal size={14} className="text-indigo-600" />
          </div>
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">Filtres</h2>
        </div>
        <div className="px-5 py-4 flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="N° bien, vendeur, acquéreur…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
            />
          </div>

          {/* Status filter pills */}
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTER_OPTIONS.map(opt => {
              const active = statusFilter.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleStatus(opt.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${active ? opt.active : opt.inactive}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${opt.dot}`} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className={thCls}>N° Bien</th>
                <th className={thCls}>Agence(s)</th>
                <th className={thCls}>Négociateur(s)</th>
                <th className={thCls}>Vendeur</th>
                <th className={thCls}>Acquéreur</th>
                <th className={thCls}>Net HT</th>
                <th className={thCls}>Statut TX</th>
                <th className={thCls}>Statut Régul</th>
                <th className={thCls}>Demandé par</th>
                <th className={thCls}>Date</th>
                <th className={thCls}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <Loader2 size={22} className="animate-spin" />
                      <span className="text-sm">Chargement…</span>
                    </div>
                  </td>
                </tr>
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-sm text-slate-400">
                    Aucune demande de régularisation trouvée
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => {
                  const reqCfg = REQUEST_STATUS_CONFIG[transaction.regul_request?.status ?? ''];
                  const txCfg = TX_STATUS_CONFIG[transaction.status] ?? { label: transaction.status, dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-100' };

                  return (
                    <tr
                      key={transaction.id}
                      className="cursor-pointer hover:bg-slate-50/70 transition-colors"
                      onClick={() => { setSelectedTransaction(transaction); setIsViewModalOpen(true); }}
                    >
                      <td className={tdCls + ' font-semibold text-slate-800'}>{transaction.property_number}</td>

                      <td className={tdCls + ' text-slate-600'}>{transaction.agencies.join(', ') || '—'}</td>

                      <td className={tdCls + ' text-slate-600 max-w-[160px]'} style={{ whiteSpace: 'normal' }}>
                        {transaction.negotiators.map(n => n.name).join(', ') || '—'}
                      </td>

                      <td className={tdCls + ' text-slate-600'}>{transaction.seller}</td>
                      <td className={tdCls + ' text-slate-600'}>{transaction.buyer}</td>

                      <td className={tdCls + ' font-bold text-emerald-700'}>{formatCurrency(calculateNetFees(transaction))}</td>

                      {/* Statut transaction */}
                      <td className={tdCls}>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${txCfg.bg} ${txCfg.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${txCfg.dot}`} />
                          {txCfg.label}
                        </span>
                      </td>

                      {/* Statut régul */}
                      <td className={tdCls}>
                        {reqCfg ? (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${reqCfg.bg} ${reqCfg.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${reqCfg.dot}`} />
                            {reqCfg.label}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>

                      {/* Demandé par */}
                      <td className={tdCls + ' text-slate-600'}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>
                            {transaction.regul_request?.user
                              ? `${transaction.regul_request.user.firstname} ${transaction.regul_request.user.lastname}`
                              : '—'}
                          </span>
                          {(transaction.total_requests ?? 0) > 1 && (
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-semibold">
                              +{(transaction.total_requests ?? 0) - 1}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className={tdCls + ' text-slate-500'}>{formatDate(transaction.regul_request?.created_at)}</td>

                      {/* Actions */}
                      <td className={tdCls} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => { setSelectedTransaction(transaction); setIsViewModalOpen(true); }}
                            className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                            title="Voir la transaction"
                          >
                            <Eye size={15} />
                          </button>

                          {transaction.regul_request?.status === 'pending' && (
                            <button
                              onClick={() => { setSelectedTransaction(transaction); setIsViewRegulModalOpen(true); }}
                              className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                              title="Traiter la demande"
                            >
                              <FileSpreadsheet size={15} />
                            </button>
                          )}

                          {transaction.regul_status === 2 && (
                            <button
                              onClick={() => { setSelectedTransaction(transaction); setIsTreatModalOpen(true); }}
                              className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                              title="Marquer comme traitée"
                            >
                              <CheckSquare size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modals ── */}
      <ViewTransactionModal
        transaction={selectedTransaction}
        isOpen={isViewModalOpen}
        onClose={() => { setIsViewModalOpen(false); setSelectedTransaction(null); }}
      />

      <ViewRegulModal
        transaction={selectedTransaction}
        isOpen={isViewRegulModalOpen}
        onClose={() => { setIsViewRegulModalOpen(false); setSelectedTransaction(null); loadData(); }}
      />

      {/* Confirmation modal */}
      {isTreatModalOpen && selectedTransaction && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                  <CheckSquare size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">Marquer comme traitée</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Régularisation #{selectedTransaction.property_number}</p>
                </div>
              </div>
              <button
                onClick={() => { setIsTreatModalOpen(false); setSelectedTransaction(null); }}
                className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors shrink-0"
              >
                <X size={18} className="text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="rounded-xl bg-indigo-50 border border-indigo-100 px-4 py-3">
                <p className="text-sm font-semibold text-indigo-800">#{selectedTransaction.property_number}</p>
                <p className="text-sm text-indigo-600 mt-0.5">{selectedTransaction.seller} → {selectedTransaction.buyer}</p>
              </div>

              <p className="text-sm text-slate-600">
                Êtes-vous sûr de vouloir marquer cette régularisation comme <strong className="text-slate-800">traitée</strong> ?
              </p>

              <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Cette action va :</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />Passer le statut à "Traitée"</li>
                  <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />Retirer la transaction de la régularisation</li>
                  <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />Finaliser le processus</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => { setIsTreatModalOpen(false); setSelectedTransaction(null); }}
                className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleMarkAsTreated(selectedTransaction as RegularizationTransaction)}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-slate-700 to-slate-900 rounded-xl hover:from-slate-800 hover:to-slate-950 shadow-sm transition-all"
              >
                <CheckSquare size={14} />
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
